import {
  FloatingContext,
  FloatingFocusManager,
  FloatingList,
  FloatingNode,
  FloatingPortal,
  ReferenceType,
} from '@floating-ui/react'
import cn from 'classnames'
import React, { useMemo } from 'react'

import MenuHeading from '../MenuHeading'
import MenuItem from '../MenuItem'
import { MenuContext } from '../lib/utils'
import styles from './DropdownMenu.module.scss'

interface DropdownMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  opened: boolean
  activeIndex: number
  setOpened: (opened: boolean) => void
  setActiveIndex: (index: number) => void
  nodeId: string
  context: FloatingContext<ReferenceType>
  elementsRef: React.MutableRefObject<HTMLElement[]>
  floatingStyles: React.CSSProperties
  getItemProps: (props?: Record<string, any>) => Record<string, any>
  portalRoot?: HTMLElement | React.MutableRefObject<HTMLElement>
  initialFocus?: number | React.MutableRefObject<HTMLElement>
  returnFocus?: boolean
  disabled?: boolean
  preventCloseOnItemClick?: boolean
}

const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      opened,
      activeIndex,
      setOpened,
      setActiveIndex,
      nodeId,
      context,
      elementsRef,
      floatingStyles,
      getItemProps,
      style,
      portalRoot,
      initialFocus,
      returnFocus,
      disabled,
      children,
      className,
      preventCloseOnItemClick = false,
      ...props
    },
    ref
  ) => {
    const modalContext = useMemo(
      () => ({
        opened,
        preventCloseOnItemClick,
        activeIndex,
        setOpened,
        setActiveIndex,
        getItemProps,
      }),
      [opened, activeIndex, getItemProps, preventCloseOnItemClick, setOpened, setActiveIndex]
    )

    return (
      <>
        <FloatingNode id={nodeId}>
          {opened && (
            <FloatingList elementsRef={elementsRef}>
              <FloatingPortal root={portalRoot}>
                <FloatingFocusManager
                  context={context}
                  initialFocus={initialFocus}
                  returnFocus={returnFocus}
                  disabled={disabled}
                  modal={false}
                >
                  <MenuContext.Provider value={modalContext}>
                    <div
                      ref={ref}
                      className={cn(styles.root, className)}
                      style={{ ...style, ...floatingStyles } as React.CSSProperties}
                      {...props}
                    >
                      {children}
                    </div>
                  </MenuContext.Provider>
                </FloatingFocusManager>
              </FloatingPortal>
            </FloatingList>
          )}
        </FloatingNode>
      </>
    )
  }
)

export default Object.assign(DropdownMenu, {
  Item: MenuItem,
  Heading: MenuHeading,
})
